import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import './TestPracticeSetsStart.css';
import studentApi from '../StudentApi';
import { FiEdit, FiArrowLeft, FiArrowRight, FiSend, FiBookmark, FiBookOpen } from 'react-icons/fi';
import Swal from 'sweetalert2';
import PracticeTimer from './PracticeTimer';

const DURATION_MINUTES = 60;

function TestPracticeSetsStart() {
    const navigate = useNavigate();
    const { practiceSetId } = useParams();

    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [markedForReview, setMarkedForReview] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const examIdRef = useRef(null);
    const TOTAL_QUESTIONS = questions.length;

    //  Timer state (stopwatch style, unlimited)
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const timerRef = useRef(null);

    //  NEW — Add start & end time tracking
    const [startTime] = useState(new Date().toISOString());

    const [endTime, setEndTime] = useState(null);

    //  Per-question timing
    const [questionTimes, setQuestionTimes] = useState({});
    const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(null);
    const location = useLocation();
    const durationMinutes = location.state?.duration || DURATION_MINUTES; // fallback to 60
    const initialQuestions = location.state?.questions;
    const maxTimeSeconds = durationMinutes * 60;
    const remainingSeconds = Math.max(maxTimeSeconds - elapsedSeconds, 0);
    console.log("Received durationMinutes:", durationMinutes, "maxTimeSeconds:", maxTimeSeconds);


    const lastSelectedAnswerRef = useRef(null);

    // Fetch questions
    useEffect(() => {
        if (!practiceSetId) {
            alert("Missing practice set ID!");
            navigate(-1);
            return;
        }

        const fetchQuestions = async () => {
            try {
                setLoading(true);
                const res = await studentApi.get(`/practice-set/${practiceSetId}/questions`);
                const extracted = res.data.data.map(item => ({
                    id: item.id,
                    exam_id: item.exam_id,
                    subject_id: item.subject_id,
                    topic_id: item.topic_id,
                    question: item.question_text,
                    options: [item.option_a, item.option_b, item.option_c, item.option_d],
                    correct_option: item.correct_option,
                    type: item.question_type === 'MCQ_RADIO' ? 'single' : 'multiple'
                }));
                setQuestions(extracted);
                if (extracted.length > 0) examIdRef.current = extracted[0].exam_id;
            } catch (err) {
                console.error(err);
                setError("Failed to load questions.");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, [practiceSetId, navigate]);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setElapsedSeconds(prev => {
                if (prev + 1 >= maxTimeSeconds) {
                    clearInterval(timerRef.current);
                    Swal.fire('Time Up!', 'Your practice set has ended.', 'info').then(() => {
                        submitExamNow();
                    });
                    return maxTimeSeconds;
                }
                return prev + 1;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [maxTimeSeconds]);


    // ✅ Track per-question time
    useEffect(() => {
        // if already tracking a question, save its spent time
        if (currentQuestionStartTime !== null) {
            const spent = Math.floor(Date.now() / 1000) - currentQuestionStartTime;
            setQuestionTimes(prev => ({
                ...prev,
                [currentIndex]: (prev[currentIndex] || 0) + spent
            }));
        }
        // start new tracking for next question
        setCurrentQuestionStartTime(Math.floor(Date.now() / 1000));
    }, [currentIndex]);

    // Helper: format elapsed time into hh:mm:ss
    const formatTime = (seconds) => {
        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    // Attempted count logic
    const attemptedCount = Object.values(selectedAnswers).filter(ans =>
        Array.isArray(ans) ? ans.length > 0 : !!ans
    ).length;
    const unattemptedCount = TOTAL_QUESTIONS - attemptedCount;


    const toMysqlDatetime = (isoString) => {
        const dt = new Date(isoString);
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        const hh = String(dt.getHours()).padStart(2, '0');
        const min = String(dt.getMinutes()).padStart(2, '0');
        const ss = String(dt.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    };

    // Inside your component, e.g., after defining `questions` and `selectedAnswers`
    const scoreObtained = questions.reduce((acc, q, idx) => {
        const sel = selectedAnswers[idx];
        if (!sel) return acc;

        if (q.type === 'single') {
            return sel === q.correct_option ? acc + 1 : acc;
        } else {
            const correctArr = Array.isArray(q.correct_option) ? q.correct_option : [q.correct_option];
            const selArr = Array.isArray(sel) ? sel : [];
            const isCorrect =
                correctArr.length === selArr.length &&
                correctArr.every(val => selArr.includes(val));
            return isCorrect ? acc + 1 : acc;
        }
    }, 0);


    // Submit exam
    const submitExamNow = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            console.log("🔹 submitExamNow called");

            // Debug: Check raw localStorage
            const rawStudentData = localStorage.getItem('studentData');
            console.log("🔹 raw localStorage studentData:", rawStudentData);

            let studentData = {};
            try {
                studentData = rawStudentData ? JSON.parse(rawStudentData) : {};
                console.log("🔹 parsed studentData:", studentData);
            } catch (parseErr) {
                console.error("❌ Failed to parse studentData from localStorage:", parseErr);
            }

            // Resolve studentId
            const studentId = parseInt(studentData.id || studentData.student_id, 10);
            console.log("🔹 resolved studentId:", studentId);

            if (!studentId) {
                console.error("❌ Student ID missing!");
                Swal.fire('Error', 'Student ID missing. Please login again.', 'error');
                navigate('/student-login');
                setIsSubmitting(false);
                return;
            }

            // Stop timer
            clearInterval(timerRef.current);

            // Store final question time before submit
            if (currentQuestionStartTime !== null) {
                const spent = Math.floor(Date.now() / 1000) - currentQuestionStartTime;
                setQuestionTimes(prev => ({
                    ...prev,
                    [currentIndex]: (prev[currentIndex] || 0) + spent
                }));
            }

            // Prepare answers payload
            const finalSelectedAnswers = lastSelectedAnswerRef.current || selectedAnswers;

            await new Promise(resolve => setTimeout(resolve, 50));
            const answersPayload = questions.map((q, idx) => {
                const sel = finalSelectedAnswers[idx];
                let isCorrect = false;

                if (q.type === 'single') {
                    isCorrect = sel === q.correct_option;
                } else {
                    const correctArr = Array.isArray(q.correct_option) ? q.correct_option : [q.correct_option];
                    const selArr = Array.isArray(sel) ? sel : [];
                    isCorrect =
                        correctArr.length === selArr.length &&
                        correctArr.every(val => selArr.includes(val));
                }

                const selectedOptionValue =
                    q.type === 'single'
                        ? sel || null
                        : Array.isArray(sel) && sel.length > 0
                            ? sel.join(',')
                            : null;

                return {
                    question_id: q.id,
                    selected_option: selectedOptionValue,
                    is_correct: isCorrect ? 1 : 0,
                    marks_obtained: isCorrect ? 1 : 0
                };
            });

            const end = new Date().toISOString();

            const payload = {
                student_id: studentId,
                exam_id: examIdRef.current,
                practice_set_id: parseInt(practiceSetId),
                subject_id: questions[0]?.subject_id || null,
                topic_id: questions[0]?.topic_id || null,
                score_obtained: scoreObtained,
                percentage: Math.round((scoreObtained / questions.length) * 100),
                total_marks: questions.length,
                passed: 1,
                answered_json: JSON.stringify(selectedAnswers),
                started_at: toMysqlDatetime(startTime),
                submitted_at: toMysqlDatetime(end),
                duration_taken: elapsedSeconds,
                answers: answersPayload
            };

            console.log("🔹 payload ready to submit:", payload);

            const res = await studentApi.post('/save-practice-result', payload);
            console.log("🔹 backend response:", res.data);

            if (res.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Exam Submitted!',
                    text: `Your practice result has been saved successfully.`,
                }).then(() => {
                   navigate(`/result/${res.data.data.id}`, {
  state: {
    questions,
    selectedAnswers,
    examId: examIdRef.current,
    topicId: questions[0]?.topic_id,
    startTime,
    endTime: new Date().toISOString(),
    examType: 'practice',
    questionTimes
  }
});

                });
            } else {
                Swal.fire('Error', res.data.error || 'Failed to save result', 'error');
            }

        } catch (error) {
            console.error('❌ Error submitting exam:', error.response || error.message);
            Swal.fire({
                icon: 'error',
                title: 'Failed to submit exam',
                text: error.response?.data?.message || 'Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };




    // Confirm before submit
    const confirmSubmit = () => {
        Swal.fire({
            title: "Submit Exam?",
            text: "You cannot change answers after submission.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Submit",
        }).then(result => {
            if (result.isConfirmed) submitExamNow();
        });
    };


    return (
        <MathJaxContext>
            <div className="tps-container">
                {loading ? (
                    <p>Loading questions...</p>
                ) : error ? (
                    <p className="tps-error">{error}</p>
                ) : questions.length === 0 ? (
                    <p>No questions found for this practice set.</p>
                ) : (
                    <div className="tps-exam-layout">
                        {/* Main Left Side */}
                        <div className="tps-main">

                            {/* Header */}
                            <div className="tps-header">
                                <h2 className="tps-title">
                                    <FiBookOpen className="tps-icon" /> Your Exam Set
                                </h2>

                                <div className="tps-timer-box">
                                    <div className="tps-timer-label">
                                        <span className="tps-live-dot"></span> ⏱ Time Elapsed
                                    </div>
                                    {/* <span className="tps-timer-digits">{formatTime(elapsedSeconds)}</span> */}
                                    <span className="tps-timer-digits">{formatTime(remainingSeconds)}</span>
                                </div>

                            </div>

                            {/* Question Box */}
                            <div className="tps-question-box">
                                <div className="tps-question-content">
                                    <h3>Q{currentIndex + 1}:</h3>
                                    <MathJax>
                                        <div dangerouslySetInnerHTML={{ __html: questions[currentIndex]?.question || '' }} />
                                    </MathJax>
                                </div>

                                {/* Options */}
                                <div className="tps-options">
                                    {questions[currentIndex]?.options?.map((opt, idx) => {
                                        const type = questions[currentIndex]?.type;
                                        const name = `tps-question-${currentIndex}`;
                                        const optionLetter = String.fromCharCode(65 + idx);
                                        const isSelected =
                                            type === "single"
                                                ? selectedAnswers[currentIndex] === optionLetter
                                                : selectedAnswers[currentIndex]?.includes(optionLetter);

                                        const handleChange = e => {
                                            if (type === 'single') {
                                                const newAnswers = { ...selectedAnswers, [currentIndex]: e.target.value };
                                                setSelectedAnswers(newAnswers);
                                                lastSelectedAnswerRef.current = newAnswers; // ✅ Save latest selection
                                            } else {
                                                const existing = selectedAnswers[currentIndex] || [];
                                                const newAnswers = {
                                                    ...selectedAnswers,
                                                    [currentIndex]: e.target.checked
                                                        ? [...existing, e.target.value]
                                                        : existing.filter(item => item !== e.target.value)
                                                };
                                                setSelectedAnswers(newAnswers);
                                                lastSelectedAnswerRef.current = newAnswers; // ✅ Save latest selection
                                            }
                                        };


                                        return (
                                            <label key={idx} className={`tps-option-button ${isSelected ? 'selected' : ''}`}>
                                                <input
                                                    type={type === 'single' ? 'radio' : 'checkbox'}
                                                    name={name}
                                                    value={optionLetter}
                                                    checked={isSelected}
                                                    onChange={handleChange}
                                                    className="tps-input"
                                                />
                                                <MathJax>
                                                    <span>
                                                        <strong>{optionLetter}.</strong>{' '}
                                                        <span dangerouslySetInnerHTML={{ __html: opt }} />
                                                    </span>
                                                </MathJax>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Navigation Buttons */}
                            <div className="tps-navigation-buttons">
                                <button onClick={() => setCurrentIndex(i => i - 1)} disabled={currentIndex === 0}>
                                    <FiArrowLeft /> Previous
                                </button>
                                <button onClick={() => setCurrentIndex(i => i + 1)} disabled={currentIndex === TOTAL_QUESTIONS - 1}>
                                    Next <FiArrowRight />
                                </button>
                                <button
                                    onClick={() => setMarkedForReview(prev => ({ ...prev, [currentIndex]: !prev[currentIndex] }))}
                                    className={`tps-marked-review ${markedForReview[currentIndex] ? 'marked' : ''}`}
                                >
                                    <FiBookmark /> {markedForReview[currentIndex] ? 'Unmark Review' : 'Mark for Review'}
                                </button>
                                <button onClick={confirmSubmit} className='tps-submit' disabled={isSubmitting}>
                                    <FiSend /> Submit
                                </button>
                            </div>

                            {/* Progress Bar */}
                            <div className="tps-progress-chart">
                                <div className="tps-bar-container">
                                    <div
                                        className="tps-bar-fill-attempted"
                                        style={{ width: `${(attemptedCount / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                    <div
                                        className="tps-bar-fill-unattempted"
                                        style={{ width: `${(unattemptedCount / TOTAL_QUESTIONS) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className="tps-progress-labels">
                                <span>✅ Attempted: {attemptedCount}</span>
                                <span>❌ Unattempted: {unattemptedCount}</span>
                            </div>
                        </div>

                        {/* Sidebar Right Side */}
                        <div className="tps-sidebar">
                            <div className="tps-question-tracker">
                                {questions.map((q, idx) => {
                                    const isAttempted =
                                        q.type === "single"
                                            ? !!selectedAnswers[idx]
                                            : (selectedAnswers[idx] && selectedAnswers[idx].length > 0);

                                    const isMarked = markedForReview[idx];
                                    const isCurrent = currentIndex === idx;

                                    let trackerClass = "tps-tracker-box";
                                    if (isAttempted) trackerClass += " attempted";
                                    if (!isAttempted) trackerClass += " unattempted";
                                    if (isMarked) trackerClass += " marked";
                                    if (isCurrent) trackerClass += " current";

                                    return (
                                        <button
                                            key={idx}
                                            className={trackerClass}
                                            onClick={() => setCurrentIndex(idx)}
                                        >
                                            {idx + 1}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MathJaxContext>
    );
}

export default TestPracticeSetsStart;
